/* eslint max-lines: off */
/* eslint-disable @typescript-eslint/no-empty-function */

import React, {
    FunctionComponent,
    Component,
    ReactNode,
    useCallback,
    useState,
    useEffect,
    useRef
} from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import * as DateFnsLocale from "date-fns/locale";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { format, getYear, parse } from "date-fns";
import range from "lodash/range";
import { Input } from "components/Novus/Input";
import { SelectComponent } from "components/Novus/Select";
import { dateTimepickerType, handleChangeType, allMonths } from "./helpers";
import { ReactComponent as Calendar } from "../../../assets/calendar.svg";
import {
    Icon,
    Main,
    DateWrapper,
    DatePickerHeader,
    Image,
    calendarDropdownStyles,
    ArrowIcon
} from "./styles";
import calenderArrow from "../../../assets/calenderArrow.svg";
import { twelveHoursWithAMPM, dayMonthDate, AM, PM } from "../constants";
import { TimeIcon } from "./TimeIcon";
import { RootState } from "../../../reducers";
import { isPathPublicViewer } from "../../../Utilities/generalUtilities";
import { SelectValue } from "../../../interfaces/Select/Value";

export type popperPlacement =
    | "bottom-end"
    | "bottom-start"
    | "top-start"
    | "top-end"
    | "top"
    | "auto"
    | "bottom"
    | "left"
    | "left-end"
    | "left-start"
    | "right"
    | "right-end"
    | "right-start";

interface Props {
    customInput?: ReactNode;
    disabled?: boolean;
    type?: dateTimepickerType;
    onChange: handleChangeType;
    useWeekdaysShort?: boolean;
    value?: Date;
    selected?: string;
    minDate?: Date;
    maxDate?: Date;
    timeIntervals?: number;
    popperPlacement?: popperPlacement;
    name?: string;
    includeDates?: Date[];
    disabledKeyboardNavigation?: boolean;
    showAMPM?: boolean;
}
interface ToggleAMorPM {
    AMorPM: string;
    onChange: handleChangeType;
    changeSelectedValue: React.Dispatch<React.SetStateAction<Date | undefined>>;
    type?: dateTimepickerType;
    selectedValue?: Date;
}

interface DateTimePickerLanguageInterface {
    locale: string;
}

const toggleAMorPM = ({
    AMorPM,
    onChange,
    changeSelectedValue,
    type,
    selectedValue
}: ToggleAMorPM): void => {
    if (type === "time" && selectedValue) {
        const currentHours = selectedValue.getHours();

        // Change time value
        if (AMorPM === AM) {
            if (currentHours > 12) {
                selectedValue.setHours(currentHours + 12);
            }
            if (currentHours === 12) {
                selectedValue.setHours(currentHours - 12);
            }
        }

        // For PM
        if (AMorPM === PM) {
            if (currentHours < 11) {
                selectedValue.setHours(currentHours - 12);
            }
            if (currentHours === 11) {
                selectedValue.setHours(currentHours + 12);
            }
        }

        // Set it in the timepicker
        changeSelectedValue(selectedValue);

        // On change passed from the source
        onChange(selectedValue, undefined);
    }
};

export const DateTimePicker: FunctionComponent<Props> = ({
    disabled,
    type,
    onChange,
    name,
    value,
    minDate,
    maxDate,
    timeIntervals,
    useWeekdaysShort,
    popperPlacement,
    includeDates,
    disabledKeyboardNavigation,
    showAMPM
}) => {
    const { t } = useTranslation();
    const calenderRef = useRef<HTMLDivElement | null>(null);

    const [selectedValue, changeSelectedValue] = useState(value);
    const [dateTimePickerPopperWidth, setdateTimePickerPopperWidth] = useState<number>(0);
    const currentUser = useSelector((state: RootState) => state.user.currentUser);

    const languages = useSelector((state: RootState) => state.MultiLingual.languageOptions);

    const [currentLanguage, updateCurrentLanguage] = useState<DateTimePickerLanguageInterface>({
        locale: "en"
    });

    const months: Array<{ label: string; value: string }> = [];

    const allYears = range(getYear(new Date()) - 10, getYear(new Date()) + 1, 1);

    const years: Array<{ label: number; value: number }> = [];

    useEffect(() => {
        allYears
            .sort((a, b) => b - a)
            .forEach(function mapYears(year) {
                years.push({
                    label: year,
                    value: year
                });
            });

        allMonths.forEach(function mapMonths(month) {
            months.push({
                label: t(month),
                value: month
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allYears, allMonths]);

    useEffect(() => {
        const useLanguage = isPathPublicViewer()
            ? navigator.language.split("-")[0]
            : currentUser && currentUser.language && currentUser.language.name;
        const languageData = languages.results.find(
            (lan: { name: string }) => lan.name === useLanguage
        );

        if (languageData) {
            updateCurrentLanguage({ locale: languageData.name });
            // eslint-disable-next-line
            // @ts-ignore
            registerLocale(languageData.name, DateFnsLocale[languageData.name]);
        }
    }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

    // Update value to be shown in date time picker whenever user selects a value
    useEffect(() => {
        changeSelectedValue(value);
    }, [value]);

    //   Fn called when user clicks on AM or PM

    const toggleAMPM = useCallback(
        (AMorPM) =>
            toggleAMorPM({
                AMorPM,
                changeSelectedValue,
                onChange,
                selectedValue,
                type
            }),
        [selectedValue, onChange, type]
    );

    const dayste = [t("Sun"), t("Mon"), t("Tue"), t("Wed"), t("Thu"), t("Fri"), t("Sat")];
    const monthste = [
        t("January"),
        t("February"),
        t("March"),
        t("April"),
        t("May"),
        t("June"),
        t("July"),
        t("August"),
        t("September"),
        t("October"),
        t("November"),
        t("December")
    ];

    const locale = {
        localize: {
            day: (n: number): string => dayste[n],
            dayPeriod: (): void => {},
            era: (): void => {},
            month: (n: number): string => monthste[n].substring(0, 3),
            ordinalNumber: (): void => {},
            quarter: (): void => {}
        },
        // eslint-disable-next-line sort-keys
        formatLong: {
            date: (): string => "mm/dd/yyyy",
            dateTime: (): void => {},
            time: (): void => {}
        }
    };

    const timeFormat = t("timeFormat") !== "timeFormat" ? t("timeFormat") : "";

    const onCalendarOpen = (): void => {
        if (calenderRef?.current) {
            setdateTimePickerPopperWidth(calenderRef.current.clientWidth);
        }
    };

    return (
        <DateWrapper
            ref={calenderRef}
            fullWidth={type === "date"}
            popperWidth={dateTimePickerPopperWidth}
        >
            <DatePicker
                customInput={
                    <InputComponent
                        disabled={disabled}
                        showAMPM={showAMPM}
                        timeFormat={timeFormat}
                        toggleAMPM={toggleAMPM}
                        type={type}
                    />
                }
                dateFormat={type === "time" ? twelveHoursWithAMPM : dayMonthDate}
                disabled={disabled}
                disabledKeyboardNavigation={disabledKeyboardNavigation}
                includeDates={includeDates}
                locale={type === "time" ? currentLanguage.locale : locale}
                maxDate={maxDate}
                minDate={minDate}
                name={name}
                popperPlacement={popperPlacement}
                renderCustomHeader={({
                    date,
                    decreaseMonth,
                    increaseMonth,
                    changeYear,
                    changeMonth
                }): JSX.Element => {
                    const monthIndex = date.getMonth();

                    return (
                        <DatePickerHeader>
                            <div className="w-100">
                                <div className="d-flex justify-content-between pb-3 pl-2">
                                    <Image
                                        alt="arrow"
                                        className="left-arrow"
                                        src={calenderArrow}
                                        onClick={decreaseMonth}
                                    />
                                    <b className="monthyear-heading">
                                        {t(allMonths[monthIndex])}
                                        <b className="pl-1">
                                            {date
                                                ? allYears[date.getFullYear()]
                                                : allYears[new Date().getFullYear()]}
                                        </b>
                                    </b>
                                    <Image
                                        alt="arrow"
                                        className="right-arrow"
                                        src={calenderArrow}
                                        onClick={increaseMonth}
                                    />
                                </div>
                                <div className="d-flex align-items-center justify-content-between">
                                    <div className="flex-content mx-2">
                                        <SelectComponent
                                            isSearchable={false}
                                            menuPlacement="bottom"
                                            options={months}
                                            value={months[date.getMonth()]}
                                            onChange={(event): void => {
                                                const optionName = event as SelectValue;
                                                const { value: monthName } = optionName;

                                                changeMonth(
                                                    allMonths.indexOf(monthName.toString())
                                                );
                                            }}
                                        />
                                    </div>
                                    <div className="flex-content">
                                        <SelectComponent
                                            isSearchable={false}
                                            menuPlacement="bottom"
                                            options={years}
                                            value={years[allYears.indexOf(date.getFullYear())]}
                                            onChange={(event): void => {
                                                const optionName = event as SelectValue;
                                                const { label } = optionName;

                                                changeYear(Number(label));
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </DatePickerHeader>
                    );
                }}
                selected={selectedValue}
                showPopperArrow={false}
                showTimeSelect={type !== "date"}
                showTimeSelectOnly={type === "time"}
                timeFormat={timeFormat}
                timeIntervals={timeIntervals}
                todayButton={type === "time" ? null : t("Today")}
                useWeekdaysShort={useWeekdaysShort}
                onCalendarOpen={onCalendarOpen}
                onChange={onChange}
            />
        </DateWrapper>
    );
};

DateTimePicker.defaultProps = {
    disabled: false,
    disabledKeyboardNavigation: true,
    includeDates: undefined,
    showAMPM: true,
    timeIntervals: 15,
    type: "date",
    useWeekdaysShort: true
};

interface InputProps {
    disabled?: boolean;
    onClick?: () => void;
    type?: dateTimepickerType;
    value?: string;
    toggleAMPM: (a: string) => void;
    timeFormat?: string;
    showAMPM?: boolean;
}

export default class InputComponent extends Component<InputProps, {}> {
    constructor(props: InputProps) {
        super(props);
        this.state = {};
    }

    render(): JSX.Element {
        const { disabled, type, onClick, value, toggleAMPM, timeFormat, showAMPM } = this.props;
        let AMorPM = "";
        let inputValue = value;

        if (type === "time" && value && inputValue) {
            if (showAMPM) {
                AMorPM = inputValue.split(" ").pop() || "";
                inputValue = value.split(" ").slice(0, -1).join(":");
            }

            if (timeFormat) {
                const parsedDate = parse(value, "hh:mm aa", new Date());
                const formattedTime = format(parsedDate, timeFormat);

                inputValue = formattedTime;
            }
        }

        return (
            <Main>
                {type === "time" &&
                    (!showAMPM ? (
                        <ArrowIcon marginright={true} />
                    ) : (
                        <TimeIcon AMorPM={AMorPM} toggleAMPM={toggleAMPM} value={value} />
                    ))}
                {type === "date" && (
                    <Icon>
                        <Calendar />
                    </Icon>
                )}
                <Input
                    className="cursor-pointer"
                    disabled={disabled}
                    readOnly={true}
                    value={inputValue}
                    onChange={(): null => null}
                    onClick={onClick}
                />
            </Main>
        );
    }
}
